// app/calculator/pricing.js

export const PRESETS = {
  hourlyRanges: {
    junior: { min: 5, max: 15, suggested: 10 },
    mid: { min: 15, max: 35, suggested: 25 },
    senior: { min: 35, max: 80, suggested: 50 },
  },

  sizeBaseHours: {
    small: 20,
    medium: 45,
    large: 80,
  },

  productMultiplier: {
    website: 0.9,
    webapp: 1.0,
    mobile: 1.1,
    dashboard: 1.2,
  },

  complexityMultiplier: {
    basic: 0.9,
    standard: 1.0,
    complex: 1.25,
  },

  unitHours: {
    s: 1.5,
    m: 3,
    l: 5,
  },

  addOnsHours: {
    // requirements
    req_ready: 0,
    req_write: 8,
    req_discovery: 6,

    // client level
    client_tech: 0,
    client_mid: 3,
    client_nontech: 6,

    // meetings
    meet_low: 1,
    meet_mid: 3,
    meet_high: 6,

    // ux
    scope_ui: 0,
    scope_uxui_lite: 10,
    scope_uxui_standard: 18,

    // brand
    brand_guidelines: 0,
    brand_logo: 4,
    brand_basickit: 10,

    // delivery
    delivery_prototype: 0,
    delivery_figma: 3,
    delivery_library: 10,

    // handoff
    handoff_basic: 2,
    handoff_detailed: 6,

    // revisions included
    rev_1: 0,
    rev_2: 2,
    rev_3: 4,
  },

  rushMultiplier: {
    normal: 1,
    rush: 1.25,
    rush_very: 1.5,
  },

  riskBufferPct: {
    r5: 0.05,
    r10: 0.1,
    r20: 0.2,
  },
};

export function computePricing(state) {
  const timelineType = state.timelineType || "normal";
  const rushMultiplier = timelineType === "rush" ? 1.25 : (timelineType === "rush_very" ? 1.5 : 1.0);
const rate = Number(state.hourlyRate || 0);

  const sizeBase = PRESETS.sizeBaseHours[state.projectSize] ?? 0;
  const pMul = PRESETS.productMultiplier[state.productType] ?? 1;
  const cMul = PRESETS.complexityMultiplier[state.complexity] ?? 1;

  const presetBaseHours = sizeBase * pMul * cMul;

  let baseHours = 0;

  if (state.pricingMode === "screen") {
    const per = PRESETS.unitHours[state.unitComplexity] ?? 0;
    baseHours = (Number(state.unitsCount || 0) || 0) * per;
  } else if (state.pricingMode === "component") {
    const per = PRESETS.unitHours[state.unitComplexity] ?? 0;
    baseHours = (Number(state.unitsCount || 0) || 0) * per;
  } else if (state.pricingMode === "fixed") {
    const fixed = Number(state.fixedHours || 0) || 0;
    baseHours = fixed > 0 ? fixed : presetBaseHours;
  } else {
    baseHours = presetBaseHours;
  }

  const addOnKeys = [
    state.requirementsState,
    state.clientLevel,
    state.meetingsRate,
    state.scopeType,
    state.brandReadiness,
    state.deliveryType,
    state.handoffLevel,
    state.includedRevisions,
  ];

  const addOnHours = addOnKeys.reduce((sum, k) => {
    return sum + (PRESETS.addOnsHours[k] ?? 0);
  }, 0);

  const totalHours = round1(baseHours + addOnHours);

  const subtotal = totalHours * rate;

  const rushMul = PRESETS.rushMultiplier[state.timelineType] ?? 1;
  const afterRush = subtotal * rushMul;

  const riskPct = PRESETS.riskBufferPct[state.riskBuffer] ?? 0.1;
  const riskAmount = afterRush * riskPct;

  const recommended = round2(afterRush + riskAmount);
  const minimum = round2(recommended * 0.9);
  const premium = round2(recommended * 1.15);

  return {
    rate,
    baseHours: round1(baseHours),
    addOnHours: round1(addOnHours),
    totalHours,
    subtotal: round2(subtotal),
    rushMultiplier: rushMul,
    afterRush: round2(afterRush),
    riskPct,
    riskAmount: round2(riskAmount),
    recommended,
    minimum,
    premium,
  };
}

function round1(n) {
  return Math.round((Number(n) || 0) * 10) / 10;
}
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
